<template>
  <div class="content">
    <h2 class="text-2xl font-bold mb-4">Sound Settings & Features</h2>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">What sound options are available?</h3>
      <p>Spin the Names includes customizable sound options to enhance your spinning experience:</p>
      <ul class="list-disc ml-6 mt-2">
        <li><strong>During-spin sounds:</strong> Audio that plays while the wheel is spinning</li>
        <li><strong>Winner sounds:</strong> Celebratory sounds that play when a winner is selected</li>
        <li><strong>Volume controls:</strong> Separate volume settings for both sound types</li>
        <li><strong>Mute option:</strong> Quickly silence all sounds when needed</li>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">How do I change the spinning sound?</h3>
      <p>To customize the sound that plays during spinning:</p>
      <ol class="list-decimal ml-6 mt-2">
        <li>Click on the settings icon or open the wheel settings panel</li>
        <li>Navigate to the "Sound" or "Audio" tab</li>
        <li>Select "During Spin Sound" options</li>
        <li>Choose from the available sound options in the dropdown menu</li>
        <li>Use the volume slider to adjust the during-spin sound volume</li>
        <li>Click "Save" or "Apply" to confirm your changes</li>
      </ol>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">How do I change the winner sound?</h3>
      <p>To customize the sound that plays when a winner is selected:</p>
      <ol class="list-decimal ml-6 mt-2">
        <li>Access the wheel settings panel</li>
        <li>Navigate to the "Sound" or "Audio" tab</li>
        <li>Select "Winner Sound" options</li>
        <li>Choose from the available winner sound effects</li>
        <li>Use the volume slider to adjust the winner sound volume</li>
        <li>Click "Save" or "Apply" to confirm your changes</li>
      </ol>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Can I control the volume of sounds?</h3>
      <p>Yes, Spin the Names offers separate volume controls for different sound types:</p>
      <ul class="list-disc ml-6 mt-2">
        <li><strong>During-spin volume:</strong> Controls only the volume of sounds playing while the wheel spins</li>
        <li><strong>Winner sound volume:</strong> Controls only the volume of the sound that plays when a winner is
          selected</li>
        <li><strong>Master volume/Mute:</strong> Quickly mute or unmute all sounds using the volume icon in the toolbar
        </li>
      </ul>
      <p class="mt-2">These separate controls allow you to customize your audio experience - for example, you might want
        a subtle sound during spinning but a more prominent celebration sound for winners.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Why aren't sounds playing?</h3>
      <p>If you're not hearing sounds when using the wheel, check the following:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>Make sure your device's volume is turned up</li>
        <li>Check if the app is muted (look for a muted volume icon in the toolbar)</li>
        <li>Verify that both volume sliders in the sound settings aren't set to zero</li>
        <li>Ensure your browser allows websites to play audio (check site permissions)</li>
        <li>Try using a different browser if the issue persists</li>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Are sounds available on all devices?</h3>
      <p>Sound features are available on most devices and modern browsers. However:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>Mobile devices may require interaction before sounds can play (due to browser policies)</li>
        <li>Some older browsers might have limited sound support</li>
        <li>School or work networks might block audio features</li>
        <li>Embedded wheels might have restricted sound capabilities</li>
      </ul>
      <p class="mt-2">The wheel will still function properly for selection purposes even if sounds are unavailable.</p>
    </div>

    <p class="mt-8 text-sm"><a href="#top" class="text-blue-500 hover:underline">Back to top</a></p>
  </div>
</template>

<script setup>
import '@components/ui/faq/faqStyles.css';
</script>
